import { useState, useEffect, useCallback } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  adminBrandsService,
  AdminBrand,
  WhiteLabelTier,
} from "../../services/admin/brands.admin.service";
import { adminService } from "../../services/admin/admin.service";
import { DataTable, DataTablePagination } from "../../components/ui/data-table";
import {
  Building2,
  Palette,
  Search,
  Loader2,
  Check,
  Minus,
  Pencil,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { DialogRoot, DialogContent, DialogHeader, DialogFooter } from "../../components/ui/dialog";
import { customJoinPageService } from "../../services/customJoinPage.service";

/** Override options, in the order they are offered. */
const TIER_OPTIONS: { value: WhiteLabelTier | null; label: string; hint: string }[] = [
  { value: null, label: "Plan", hint: "Follow the brand's subscription (default)" },
  { value: "NONE", label: "None", hint: "Force-deny regardless of plan" },
  { value: "BASIC", label: "Basic", hint: "Force-grant portal name + logo" },
  { value: "FULL", label: "Full", hint: "Force-grant full customization" },
];

/**
 * Admin control for white-label portal access.
 *
 * White-label is normally earned by plan — Growth ($99) grants portal branding,
 * Scale ($249) grants full customization. This screen only sets an *override*
 * for the cases the plan ladder can't express: pilot brands, legacy/custom
 * plans, and shutting off abuse. "Plan" (the default) means no override.
 *
 * Going live is still the brand's own decision — the Live column reflects that
 * switch, which brands flip from their own settings. Overriding never clears a
 * brand's saved portal name or logo.
 */
export default function BrandWhiteLabel() {
  const [brands, setBrands] = useState<AdminBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updatingBrandId, setUpdatingBrandId] = useState<string | null>(null);
  const [editingSenderId, setEditingSenderId] = useState<string | null>(null);
  const [senderDraft, setSenderDraft] = useState("");
  const [savingSenderId, setSavingSenderId] = useState<string | null>(null);

  // Custom join page: the admin's only control is on/off. Switching off asks
  // for an optional note the brand sees in its editor.
  const [customPageBusyId, setCustomPageBusyId] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<AdminBrand | null>(null);
  const [disableNote, setDisableNote] = useState("");

  const handleSetCustomPage = async (
    brand: AdminBrand,
    enabled: boolean,
    note?: string | null
  ) => {
    setCustomPageBusyId(brand.id);
    try {
      const result = await customJoinPageService.adminSetEnabled(brand.id, enabled, note);
      toast.success(
        enabled
          ? `Custom join page enabled for ${brand.companyName || brand.username}`
          : "Custom join page disabled — the standard page is showing again"
      );
      setBrands((current) =>
        current.map((b) =>
          b.id === brand.id
            ? {
                ...b,
                customJoinPage: {
                  enabled: result.enabled,
                  status: result.status,
                  publishedVersion: result.publishedVersion,
                  note: result.note,
                  openReports: b.customJoinPage?.openReports ?? 0,
                },
              }
            : b
        )
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update custom join page");
    } finally {
      setCustomPageBusyId(null);
      setDisableTarget(null);
      setDisableNote("");
    }
  };

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      if (searchQuery) {
        params.keyword = searchQuery;
        params.searchBy = "COMPANY";
      }

      const response = await adminBrandsService.searchBrands(params);
      setBrands(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleSetTier = async (brandId: string, tier: WhiteLabelTier | null) => {
    setUpdatingBrandId(brandId);
    try {
      await adminService.setBrandWhiteLabelTier(brandId, tier);
      toast.success(
        tier === null
          ? "Override cleared — this brand now follows its plan"
          : `White-label override set to ${tier} — saved branding is kept`
      );

      setBrands((current) =>
        current.map((brand) =>
          brand.id === brandId ? { ...brand, whiteLabelAdminTierOverride: tier } : brand
        )
      );
    } catch (error) {
      console.error("Error updating white-label access:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to update white-label access"
      );
    } finally {
      setUpdatingBrandId(null);
    }
  };

  // Post-checkout conversion: preview first (a count and a few emails), then
  // an explicit second click performs the bulk write.
  const [conversionPreview, setConversionPreview] = useState<{
    brandId: string;
    live: boolean;
    count: number;
    sample: string[];
  } | null>(null);
  const [conversionBusyId, setConversionBusyId] = useState<string | null>(null);

  const handlePreviewConversion = async (brandId: string) => {
    setConversionBusyId(brandId);
    try {
      const preview = await adminService.previewBrandPortalConversion(brandId);
      setConversionPreview({ brandId, ...preview });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to load conversion preview"
      );
    } finally {
      setConversionBusyId(null);
    }
  };

  const handleConfirmConversion = async (brandId: string) => {
    setConversionBusyId(brandId);
    try {
      const result = await adminService.convertBrandPostCheckoutAffiliates(brandId);
      toast.success(
        result.converted === 0
          ? "Nothing to convert"
          : `${result.converted} post-checkout affiliate${
              result.converted === 1 ? "" : "s"
            } now belong to this brand's portal`
      );
      setConversionPreview(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to convert affiliates"
      );
    } finally {
      setConversionBusyId(null);
    }
  };

  const handleSaveSender = async (brandId: string) => {
    const address = senderDraft.trim() || null;
    setSavingSenderId(brandId);
    try {
      await adminService.setBrandWhiteLabelEmailSender(brandId, address);
      toast.success(
        address
          ? `Branded emails will send from ${address}`
          : "Reverted to the platform sender"
      );
      setBrands((current) =>
        current.map((brand) =>
          brand.id === brandId
            ? { ...brand, whiteLabelEmailFromAddress: address }
            : brand
        )
      );
      setEditingSenderId(null);
    } catch (error) {
      console.error("Error updating email sender:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to update email sender"
      );
    } finally {
      setSavingSenderId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBrands();
  };

  const columns: ColumnDef<AdminBrand>[] = [
    {
      id: "brand",
      header: "Brand",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center overflow-hidden">
            {row.original.brandProfileImage ? (
              <img
                src={row.original.brandProfileImage}
                alt={row.original.companyName || "Brand"}
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
          <span className="font-medium text-foreground truncate">
            {row.original.companyName || "Unnamed Brand"}
          </span>
        </div>
      ),
      meta: {
        width: "w-64",
        skeleton: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="h-3.5 w-32 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "website",
      header: "Website",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.website || "-"}
        </span>
      ),
      // Sized to its longest value; the table scrolls rather than clipping.
      meta: { fit: true },
    },
    {
      id: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-sm text-foreground">{row.original.email}</span>
      ),
      meta: { fit: true },
    },
    {
      id: "joinedAt",
      header: "Joined Date",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {format(new Date(row.original.joinedAt), "MMM d, yyyy")}
        </span>
      ),
      meta: { fit: true },
    },
    {
      id: "live",
      header: "Live",
      cell: ({ row }) => (
        <div className="flex justify-center" title="The brand's own switch">
          {row.original.whiteLabelEnabledByBrand ? (
            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <Minus className="h-4 w-4 text-muted-foreground/50" />
          )}
        </div>
      ),
      meta: {
        width: "w-20",
        headerClassName: "text-center",
        skeleton: (
          <div className="flex justify-center">
            <div className="h-4 w-4 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "emailSender",
      header: "Email Sender",
      cell: ({ row }) => {
        const brand = row.original;
        const saving = savingSenderId === brand.id;

        if (editingSenderId === brand.id) {
          return (
            <div className="flex items-center gap-1.5">
              <input
                type="email"
                value={senderDraft}
                onChange={(e) => setSenderDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveSender(brand.id);
                  if (e.key === "Escape") setEditingSenderId(null);
                }}
                placeholder="partners@brand.com"
                autoFocus
                disabled={saving}
                className="w-44 px-2 py-1 text-xs border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
              />
              <button
                type="button"
                title="Save (the domain must already be verified in Postmark)"
                disabled={saving}
                onClick={() => handleSaveSender(brand.id)}
                className="p-1 rounded text-emerald-600 hover:bg-muted disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                title="Cancel"
                disabled={saving}
                onClick={() => setEditingSenderId(null)}
                className="p-1 rounded text-muted-foreground hover:bg-muted disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs text-muted-foreground"
              title={
                brand.whiteLabelEmailFromAddress ||
                "Branded emails use the platform sender with the brand's display name"
              }
            >
              {brand.whiteLabelEmailFromAddress || "Platform default"}
            </span>
            <button
              type="button"
              title="Set a verified per-brand From address (requires sender/domain verification in Postmark first)"
              onClick={() => {
                setEditingSenderId(brand.id);
                setSenderDraft(brand.whiteLabelEmailFromAddress || "");
              }}
              className="p-1 rounded text-muted-foreground hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      },
      meta: {
        width: "w-56",
        // A verified sender address is shown whole; the table scrolls.
        fit: true,
        skeleton: <div className="h-3.5 w-40 rounded bg-muted animate-pulse" />,
      },
    },
    {
      id: "conversion",
      header: "Post-checkout affiliates",
      cell: ({ row }) => {
        const brandId = row.original.id;
        const busy = conversionBusyId === brandId;
        const preview =
          conversionPreview?.brandId === brandId ? conversionPreview : null;
        if (!row.original.whiteLabelEnabledByBrand) {
          return (
            <span
              className="text-xs text-muted-foreground"
              title="Only a brand with a live portal can take over its post-checkout affiliates"
            >
              Portal not live
            </span>
          );
        }
        if (preview) {
          return (
            <div className="space-y-1.5 text-xs">
              <p className="text-foreground">
                {preview.count === 0
                  ? "No convertible affiliates"
                  : `${preview.count} incomplete post-checkout affiliate${
                      preview.count === 1 ? "" : "s"
                    } with no other brand`}
              </p>
              {preview.sample.length > 0 && (
                <p className="text-muted-foreground truncate max-w-[16rem]">
                  {preview.sample.join(", ")}
                  {preview.count > preview.sample.length ? ", …" : ""}
                </p>
              )}
              <div className="flex gap-2">
                {preview.count > 0 && (
                  <button
                    type="button"
                    onClick={() => handleConfirmConversion(brandId)}
                    disabled={busy}
                    className="px-2 py-1 rounded bg-[#d7785b] text-white hover:bg-[#c56a4f] disabled:opacity-50"
                  >
                    {busy ? "Converting…" : `Convert ${preview.count}`}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConversionPreview(null)}
                  disabled={busy}
                  className="px-2 py-1 rounded border border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        }
        return (
          <button
            type="button"
            onClick={() => handlePreviewConversion(brandId)}
            disabled={busy}
            className="text-xs px-2 py-1 rounded border border-border text-foreground hover:bg-muted disabled:opacity-50"
            title="Move incomplete post-checkout affiliates that exist only for this brand under its portal. Existing marketplace affiliates are never touched."
          >
            {busy ? "Checking…" : "Preview conversion"}
          </button>
        );
      },
      meta: {
        width: "w-56",
        skeleton: <div className="h-6 w-32 rounded bg-muted animate-pulse" />,
      },
    },
    {
      id: "customPage",
      header: "Custom page",
      cell: ({ row }) => {
        const cp = row.original.customJoinPage ?? null;
        const enabled = Boolean(cp?.enabled);
        const busy = customPageBusyId === row.original.id;
        const status = !enabled
          ? "Off"
          : cp?.status === "PUBLISHED"
            ? `Live · v${cp.publishedVersion}`
            : "Enabled, not published";
        return (
          <div className="flex items-center justify-center gap-3">
            <Switch
              checked={enabled}
              disabled={busy}
              aria-label="Custom join page"
              onCheckedChange={(next) =>
                next ? handleSetCustomPage(row.original, true) : setDisableTarget(row.original)
              }
            />
            <div className="text-xs leading-tight">
              <div className={enabled ? "text-foreground" : "text-muted-foreground"}>{status}</div>
              {cp && cp.openReports > 0 && (
                <div className="text-destructive">
                  {cp.openReports} open report{cp.openReports === 1 ? "" : "s"}
                </div>
              )}
            </div>
            {busy && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />}
          </div>
        );
      },
      meta: {
        width: "w-52",
        headerClassName: "text-center",
        skeleton: (
          <div className="flex justify-center">
            <div className="h-6 w-32 rounded-full bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "override",
      header: "Access Override",
      cell: ({ row }) => {
        const current = row.original.whiteLabelAdminTierOverride ?? null;
        const busy = updatingBrandId === row.original.id;

        return (
          <div className="flex items-center justify-center gap-2">
            <div
              role="radiogroup"
              aria-label="White-label access override"
              className="inline-flex rounded-lg border border-border overflow-hidden"
            >
              {TIER_OPTIONS.map((option) => {
                const selected = current === option.value;
                return (
                  <button
                    key={option.label}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    title={option.hint}
                    disabled={busy}
                    onClick={() => handleSetTier(row.original.id, option.value)}
                    className={`px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      selected
                        ? "bg-[#d7785b] text-white"
                        : "bg-card text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {busy && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />}
          </div>
        );
      },
      meta: {
        width: "w-72",
        headerClassName: "text-center",
        skeleton: (
          <div className="flex justify-center">
            <div className="h-7 w-48 rounded-lg bg-muted animate-pulse" />
          </div>
        ),
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Palette className="h-7 w-7 text-[#d7785b]" />
            Brand White-Label
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            White-label is granted by plan — Growth ($99) for portal branding,
            Scale ($249) for full customization. Use an override only for pilots,
            legacy plans, or to shut off abuse. Overrides never erase a brand's
            saved branding.
          </p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="bg-card border border-border rounded-xl p-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by company name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20 focus:border-[#d7785b]"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-[#d7785b] text-white rounded-lg hover:bg-[#c56a4f] transition-colors font-medium"
          >
            Search
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={brands}
          isLoading={loading}
          skeletonRows={5}
          getRowId={(row) => row.id}
          emptyText={
            <span className="inline-block">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
              <span className="block text-lg font-medium">No brands found</span>
              <span className="block text-sm mt-1">
                Try adjusting your search criteria
              </span>
            </span>
          }
        />

        {/* Pagination */}
        {!loading && brands.length > 0 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={setPage}
            noun="brands"
          />
        )}
      </div>

      <DialogRoot
        open={disableTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDisableTarget(null);
            setDisableNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>Switch off the custom join page?</DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {disableTarget?.companyName || disableTarget?.username} goes back to the standard join
            page immediately. Their draft and published versions are kept, and the editor tab
            disappears until you switch it on again.
          </p>
          <label className="block text-sm font-medium mb-1" htmlFor="custom-page-note">
            Note to the brand (optional)
          </label>
          <textarea
            id="custom-page-note"
            value={disableNote}
            onChange={(e) => setDisableNote(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. Please remove the third-party logo before we re-enable this."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={customPageBusyId !== null}
              onClick={() =>
                disableTarget && handleSetCustomPage(disableTarget, false, disableNote.trim() || null)
              }
            >
              Switch off
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </div>
  );
}
